import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Check, Copy, Eye, EyeOff } from 'lucide-react';
import * as Sentry from '@sentry/react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

export interface CopyFieldProps {
  label: string;
  value: string;
  hint?: string;
  sensitive?: boolean;
  disableReveal?: boolean;
}

export function CopyField({
  label,
  value,
  hint,
  sensitive = false,
  disableReveal = false,
}: CopyFieldProps): ReactElement {
  const [visible, setVisible] = useState(!sensitive);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return (): void => {
      if (copiedTimerRef.current !== null) {
        clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const handleCopy = async (): Promise<void> => {
    setError(null);
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (copiedTimerRef.current !== null) {
        clearTimeout(copiedTimerRef.current);
      }
      copiedTimerRef.current = setTimeout(() => {
        copiedTimerRef.current = null;
        setCopied(false);
      }, 2000);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { error_type: 'clipboard_write' },
        extra: { label },
      });
      setError('Copy failed — select and copy manually.');
    }
  };

  const displayValue = sensitive && !visible ? '••••••••••••••••••••' : value;

  return (
    <div className="space-y-1">
      {label && (
        <Label className="text-xs text-muted-foreground">{label}</Label>
      )}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <code className="block w-full rounded-md border bg-muted px-3 py-2 text-xs font-mono break-all leading-relaxed pr-8">
            {value ? (
              displayValue
            ) : (
              <span className="text-muted-foreground italic">
                Not available yet
              </span>
            )}
          </code>
          {value && sensitive && (
            <button
              type="button"
              onClick={() => setVisible(v => !v)}
              title={visible ? 'Hide' : 'Show'}
              aria-label={visible ? 'Hide value' : 'Show value'}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              disabled={disableReveal}
            >
              {visible ? (
                <EyeOff className="h-3.5 w-3.5" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
            </button>
          )}
        </div>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 h-8 w-8"
            onClick={handleCopy}
            title="Copy"
            aria-label={copied ? 'Copied to clipboard' : 'Copy to clipboard'}
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

CopyField.displayName = 'CopyField';
